import React from "react";
import {
  APPLICATION_BUILD_NUMBER,
  APPLICATION_VERSION,
} from "../../utils/applicationVersion";

const MdmSidebarNav = ({
  showUserManagement = false,
  showPermissionManagement = false,
  retailerNames,
  allDevices,
  expandedRetailers,
  expandedBranches,
  selectedFolderId,
  allRetailerDevicesFolderId,
  allDevicesFolderId,
  getBranchKey,
  toggleRetailer,
  toggleBranch,
  selectRetailerAllDevices,
  selectBranch,
  getRetailerMountedFolders,
  getBranchMountedFolders,
  createNewFolder,
  getFolderKey,
  getChildFolders,
  expandedFolders,
  toggleFolder,
  deleteFolder,
  setExpandedFolders,
  setSelectedFolderId,
  setMoveTarget,
}) => {
  const renderFolderNode = (folder, depth = 0) => {
    const folderId = getFolderKey(folder);
    const children = getChildFolders(folderId);
    const isOpen = Boolean(expandedFolders[folderId]);
    const isActive = selectedFolderId === folderId;
    return (
      <div key={folderId} className="space-y-1">
        <div className="min-w-0 w-full" style={{ marginLeft: `${depth * 12}px` }}>
          <div className="flex items-center gap-0.5 min-w-0 w-full">
            <button
              type="button"
              className="min-w-0 flex-1 flex items-center py-0.5 px-2 text-on-surface-variant hover:text-primary text-left"
              onClick={() => {
                if (children.length > 0) {
                  toggleFolder(folderId);
                }
                setSelectedFolderId(folderId);
                setMoveTarget(`folder::${folderId}`);
              }}
            >
              <span className="material-symbols-outlined text-sm mr-2 shrink-0">
                {children.length > 0 ? (isOpen ? "keyboard_arrow_down" : "chevron_right") : "chevron_right"}
              </span>
              <span className="material-symbols-outlined text-sm mr-2 shrink-0">folder</span>
              <span className={`truncate ${isActive ? "font-semibold text-primary" : ""}`}>
                {folder?.name || folderId}
              </span>
            </button>
            <button
              type="button"
              className="p-1 rounded shrink-0 text-on-surface-variant hover:bg-error-container/30 hover:text-error transition-colors"
              title="Delete folder"
              aria-label={`Delete folder ${folder?.name || folderId}`}
              onClick={(e) => {
                e.stopPropagation();
                deleteFolder(folderId, folder?.name);
              }}
            >
              <span className="material-symbols-outlined text-sm">delete</span>
            </button>
          </div>
          <button
            type="button"
            className="ml-9 mt-0.5 px-1.5 py-0 rounded text-[10px] font-semibold text-on-surface-variant hover:bg-primary/10 hover:text-primary transition-colors"
            title="Create subfolder"
            aria-label={`Create subfolder under ${folder?.name || folderId}`}
            onClick={(e) => {
              e.stopPropagation();
              createNewFolder({ parentId: folderId });
              setExpandedFolders((prev) => ({ ...prev, [folderId]: true }));
            }}
          >
            + Folder
          </button>
        </div>
        {isOpen && children.map((child) => renderFolderNode(child, depth + 1))}
      </div>
    );
  };

  return (
    <aside className="hidden md:flex flex-col h-screen w-64 left-0 top-0 sticky overflow-hidden bg-surface-container-low text-xs font-medium transition-all duration-200 ease-in-out py-4 space-y-2 z-50">
      <div className="login-header flex justify-center py-0">
        <img src="/favicon.ico" alt="Flavi Icon" className="h-1 w-1 object-contain" />
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto space-y-1 px-3">
        <div className="space-y-1">
          {showUserManagement && (
            <button
              type="button"
              className="w-full flex items-center px-3 py-2 mt-2 text-on-surface-variant hover:text-primary hover:bg-surface-container-high rounded-lg transition-colors"
              onClick={() => window.open("/UserManagement", "_blank")}
            >
              <span className="material-symbols-outlined mr-3 text-lg">manage_accounts</span>
              <span>User Management</span>
            </button>
          )}
          {showPermissionManagement && (
            <button
              type="button"
              className="w-full flex items-center px-3 py-2 text-on-surface-variant hover:text-primary hover:bg-surface-container-high rounded-lg transition-colors"
              onClick={() => window.open("/PermissionManagement", "_blank")}
            >
              <span className="material-symbols-outlined mr-3 text-lg">admin_panel_settings</span>
              <span>Permission Management</span>
            </button>
          )}
          <button className="w-full flex items-center px-3 py-2 bg-primary-fixed text-primary font-semibold rounded-lg group">
            <span
              className="material-symbols-outlined mr-3 text-lg"
              style={{ fontVariationSettings: '"FILL" 1' }}
            >
              devices
            </span>
            <span>MDM - Devices</span>
          </button>
          <div className="ml-4 mt-1 space-y-1 border-l border-outline-variant border-opacity-20 pl-2">
            {retailerNames.length === 0 && (
              <div className="py-0.5 px-2 text-on-surface-variant opacity-70">No retailers</div>
            )}
            {retailerNames.map((retailerName) => {
              const branches = Object.keys(allDevices?.[retailerName] || {});
              const retailerOpen = Boolean(expandedRetailers[retailerName]);
              const retailerFolders = getRetailerMountedFolders(retailerName);

              return (
                <div key={retailerName}>
                  <button
                    className="w-full flex items-center py-0.5 px-2 text-on-surface-variant hover:text-primary cursor-pointer"
                    onClick={() => toggleRetailer(retailerName)}
                  >
                    <span className="material-symbols-outlined text-sm mr-2">
                      {retailerOpen ? "keyboard_arrow_down" : "chevron_right"}
                    </span>
                    <span className="material-symbols-outlined text-sm mr-2">store</span>
                    <span>{retailerName}</span>
                  </button>

                  {retailerOpen && (
                    <div className="space-y-1 ml-4">
                      {branches.length === 0 && (
                        <div className="py-0.5 px-2 text-on-surface-variant opacity-70">No branches</div>
                      )}
                      <button
                        type="button"
                        className={`w-full py-0.5 px-2 rounded-md font-semibold flex items-center transition-colors ${selectedFolderId === allRetailerDevicesFolderId(retailerName)
                          ? "bg-secondary-container text-on-secondary-container"
                          : "bg-surface-container-high text-on-surface hover:bg-surface-container-highest"
                          }`}
                        onClick={() => selectRetailerAllDevices(retailerName)}
                      >
                        <span className="material-symbols-outlined text-sm mr-2">inventory_2</span>
                        <span>All Branches</span>
                      </button>
                      {branches.map((branchName) => {
                        const branchKey = getBranchKey(retailerName, branchName);
                        const branchOpen = Boolean(expandedBranches[branchKey]);
                        const isActive = selectedFolderId === allDevicesFolderId(branchKey);
                        const branchFolders = getBranchMountedFolders(branchKey);

                        return (
                          <div key={branchKey}>
                            <button
                              className="w-full flex items-center py-0.5 px-2 text-on-surface-variant hover:text-primary cursor-pointer"
                              onClick={() => toggleBranch(retailerName, branchName)}
                            >
                              <span className="material-symbols-outlined text-sm mr-2">
                                {branchOpen ? "keyboard_arrow_down" : "chevron_right"}
                              </span>
                              <span className="material-symbols-outlined text-sm mr-2">location_on</span>
                              <span>{branchName}</span>
                            </button>
                            {branchOpen && (
                              <div className="ml-6 space-y-1">
                                <button
                                  type="button"
                                  className={`w-full py-0.5 px-2 rounded-md font-semibold flex items-center transition-colors ${isActive
                                    ? "bg-secondary-container text-on-secondary-container"
                                    : "bg-surface-container-high text-on-surface hover:bg-surface-container-highest"
                                    }`}
                                  onClick={() => selectBranch(retailerName, branchName)}
                                >
                                  <span className="material-symbols-outlined text-sm mr-2">devices</span>
                                  <span>All Devices</span>
                                </button>
                                {branchFolders.map((folder) => renderFolderNode(folder))}
                                <button
                                  type="button"
                                  className="w-full text-left text-[11px] px-2 py-0 rounded bg-surface-container-high text-on-surface hover:bg-surface-container-highest transition-colors"
                                  onClick={() =>
                                    createNewFolder({
                                      mountType: "branch",
                                      mountKey: branchKey,
                                    })
                                  }
                                >
                                  + Folder
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="pt-1 space-y-1">
                        {retailerFolders.map((folder) => renderFolderNode(folder))}
                        <button
                          type="button"
                          className="w-full text-left text-[11px] px-2 py-0 rounded bg-surface-container-high text-on-surface hover:bg-surface-container-highest transition-colors"
                          onClick={() =>
                            createNewFolder({
                              mountType: "retailer",
                              mountKey: retailerName,
                            })
                          }
                        >
                          + Folder
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </nav>

      <div className="shrink-0 px-3 py-3 text-[10px] font-medium text-on-surface-variant/90 tabular-nums">
        Version {APPLICATION_VERSION}
        <span className="mx-1.5 text-on-surface-variant/40" aria-hidden="true">
          |
        </span>
        Build {APPLICATION_BUILD_NUMBER}
      </div>
    </aside>
  );
};

export default MdmSidebarNav;
